/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React, { Fragment, useRef } from "react";
import type { UserMessage } from "@spwn/types/firebase/firestore";
import { Box, SvgIcon } from "@material-ui/core";
import { withNoTranslate } from "i18n/noTranslate";
import { StreamingCommentBanDialog } from "./StreamingCommentBanDialog";
import { StreamingCommentBlockDialog } from "./StreamingCommentBlockDialog";

interface Props {
  message: UserMessage;
  isCommentUser: boolean;
  isChatAdmin: boolean;
  isDarkModeEnabled: boolean;
  isMenuOpen: boolean;
  onOpenMenu: () => void;
  onCloseMenu: () => void;
  onClickBanUser: (comment: Pick<UserMessage, "_id" | "uid">) => Promise<void>;
  onClickBanComment: (idx: string) => Promise<void>;
  onClickBlockUser:
    | ((comment: Pick<UserMessage, "uid" | "name">) => Promise<void>)
    | undefined;
  msgDeleteFunction: (idx: string) => void;
}
export const CommentItemNormalV2: React.FC<Props> = (props) => {
  const ref = useRef(null);
  const isEnabledBanDialog =
    (props.isChatAdmin || !!props.onClickBlockUser) && !props.isCommentUser;

  return (
    <Fragment>
      {isEnabledBanDialog &&
        (props.isChatAdmin ? (
          <StreamingCommentBanDialog
            target={ref}
            banTargetComment={props.isMenuOpen ? props.message : undefined}
            disableUserBlock={props.isCommentUser}
            onClose={props.onCloseMenu}
            onClickBanUser={props.onClickBanUser}
            onClickBanComment={props.onClickBanComment}
          />
        ) : props.onClickBlockUser ? (
          <StreamingCommentBlockDialog
            target={ref}
            targetComment={props.isMenuOpen ? props.message : undefined}
            onClose={props.onCloseMenu}
            onClickBlockUser={props.onClickBlockUser}
          />
        ) : null)}
      <div
        ref={ref}
        className={"streaming_comment_item"}
        css={css`
          display: grid;
          grid-template-columns: max-content 1fr max-content;
          align-items: center;
          padding: 4px 12px;
          ${isEnabledBanDialog
            ? `
          cursor: pointer;
          &:hover {
            background-color: ${props.isDarkModeEnabled ? "#444" : "rgba(0, 0, 0, 0.05)"};
          }`
            : undefined}
          ${isEnabledBanDialog && props.isMenuOpen
            ? `background-color: ${props.isDarkModeEnabled ? "#444" : "rgba(0, 0, 0, 0.05)"}`
            : undefined}
        `}
        onClick={isEnabledBanDialog ? props.onOpenMenu : undefined}
      >
        {/* ユーザーアイコンを表示 */}
        <Box paddingRight={"8px"}>
          <img
            src={props.message.icon}
            alt={props.message.name}
            className="icon"
            css={css`
              display: block;
              width: 24px;
              height: 24px;
              background-color: #fff;
              border-radius: 50%;
              aspect-ratio: 1 / 1;
              object-fit: cover;
            `}
          />
        </Box>

        {/* ユーザーコメントを表示 */}
        <Box
          color={
            props.isDarkModeEnabled ? "dark.text.primary" : "light.text.primary"
          }
          fontSize={"12px"}
          lineHeight={"1.5em"}
          {...withNoTranslate()}
        >
          <Box component={"span"} marginRight={"8px"} fontWeight={"bold"}>
            {props.message.name}
          </Box>
          {props.message.msg}
        </Box>

        {props.isCommentUser && (
          // コメント削除ボタンを表示
          <Box
            style={{ cursor: "pointer" }}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            onClick={() => props.msgDeleteFunction(props.message._id!)}
          >
            <CrossIcon />
          </Box>
        )}
      </div>
    </Fragment>
  );
};

const CrossIcon = () => {
  return (
    <SvgIcon viewBox="0 0 24 24">
      <g transform="translate(6,6)">
        <path
          d="M5 3.88906L8.88906 0L10 1.11094L6.11094 5L10 8.88906L8.88906 10L5 6.11094L1.11094 10L0 8.88906L3.88906 5L0 1.11094L1.11094 0L5 3.88906Z"
          fill="#A3A3A3"
        />
      </g>
    </SvgIcon>
  );
};
